import { useState, useCallback, useEffect, useRef } from "react";
import { Search, Building2, Loader2, MapPin, CheckCircle2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

// Swiss cantons
const SWISS_CANTONS = [
  { code: "ZH", name: "Zürich" },
  { code: "BE", name: "Bern" },
  { code: "LU", name: "Luzern" },
  { code: "UR", name: "Uri" },
  { code: "SZ", name: "Schwyz" },
  { code: "OW", name: "Obwalden" },
  { code: "NW", name: "Nidwalden" },
  { code: "GL", name: "Glarus" },
  { code: "ZG", name: "Zug" },
  { code: "FR", name: "Fribourg" },
  { code: "SO", name: "Solothurn" },
  { code: "BS", name: "Basel-Stadt" },
  { code: "BL", name: "Basel-Landschaft" },
  { code: "SH", name: "Schaffhausen" },
  { code: "AR", name: "Appenzell Ausserrhoden" },
  { code: "AI", name: "Appenzell Innerrhoden" },
  { code: "SG", name: "St. Gallen" },
  { code: "GR", name: "Graubünden" },
  { code: "AG", name: "Aargau" },
  { code: "TG", name: "Thurgau" },
  { code: "TI", name: "Ticino" },
  { code: "VD", name: "Vaud" },
  { code: "VS", name: "Valais" },
  { code: "NE", name: "Neuchâtel" },
  { code: "GE", name: "Genève" },
  { code: "JU", name: "Jura" },
];

// UID/MWST pattern detection
const UID_PATTERN = /^CHE[-\s]?\d{3}[.\s]?\d{3}[.\s]?\d{3}/i;
const PARTIAL_UID_PATTERN = /^CHE[-\s]?\d/i;

export interface RegistryCompany {
  name: string;
  uid: string;
  legalForm: string;
  status: string;
  address: {
    street: string;
    city: string;
    zip: string;
    canton: string;
    country: string;
  };
  vatNumber: string;
  mwstRegistered?: boolean;
  source: string;
}

interface SmartCompanyInputProps {
  /** Callback when a company is selected from the registry */
  onSelect: (company: RegistryCompany) => void;
  /** Default canton code (e.g., "ZH") */
  defaultCanton?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Show canton selector */
  showCantonSelector?: boolean;
}

/**
 * SmartCompanyInput - A unified search input for Swiss company lookup.
 *
 * - Accepts UID (CHE-XXX.XXX.XXX), MWST number, or company name
 * - Auto-detects input type and queries appropriate API
 * - Shows search results in a dropdown
 * - On selection, returns full company data for auto-fill
 */
export function SmartCompanyInput({
  onSelect,
  defaultCanton,
  placeholder = "Enter company name, UID, or MWST number...",
  disabled = false,
  className,
  showCantonSelector = true,
}: SmartCompanyInputProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [canton, setCanton] = useState(defaultCanton ?? "ZH");
  const [inputType, setInputType] = useState<"name" | "uid">("name");
  const [hasInitialized, setHasInitialized] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch user's preferred canton if no explicit default provided
  const { data: userSettings } = trpc.userSettings.get.useQuery(undefined, {
    enabled: !defaultCanton, // Only fetch if no explicit default
  });

  // Persist canton changes to user settings
  const updateSettings = trpc.userSettings.update.useMutation();

  // Initialize canton from user settings if no explicit default
  useEffect(() => {
    if (!hasInitialized && !defaultCanton && userSettings?.lastUsedCanton) {
      setCanton(userSettings.lastUsedCanton);
      setHasInitialized(true);
    }
  }, [defaultCanton, userSettings?.lastUsedCanton, hasInitialized]);

  // Handle canton change - persist to user settings
  const handleCantonChange = useCallback(
    (newCanton: string) => {
      setCanton(newCanton);
      // Persist the new canton preference (fire and forget)
      if (!defaultCanton) {
        updateSettings.mutate({ lastUsedCanton: newCanton });
      }
    },
    [defaultCanton, updateSettings]
  );

  // Detect input type
  useEffect(() => {
    if (PARTIAL_UID_PATTERN.test(query)) {
      setInputType("uid");
    } else {
      setInputType("name");
    }
  }, [query]);

  // Search by name query (debounced)
  const searchQuery = trpc.company.searchRegistry.useQuery(
    { query, limit: 10 },
    {
      enabled: inputType === "name" && query.length >= 3 && open,
      staleTime: 60000, // Cache for 1 minute
    }
  );

  // Lookup by UID query
  const uidQuery = trpc.company.lookupByUid.useQuery(
    { uid: query },
    {
      enabled: inputType === "uid" && UID_PATTERN.test(query) && open,
      staleTime: 300000, // Cache for 5 minutes
    }
  );

  const isLoading = searchQuery.isLoading || uidQuery.isLoading;
  const results =
    inputType === "uid"
      ? uidQuery.data
        ? [uidQuery.data]
        : []
      : (searchQuery.data ?? []);

  // Handle input change with debouncing for name search
  const handleInputChange = useCallback((value: string) => {
    setQuery(value);

    // Open dropdown when typing
    if (value.length >= 3) {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      debounceTimeout.current = setTimeout(() => {
        setOpen(true);
      }, 150);
    } else {
      setOpen(false);
    }
  }, []);

  // Handle company selection
  const handleSelect = useCallback(
    (company: RegistryCompany) => {
      setQuery(company.name);
      setOpen(false);
      onSelect(company);
    },
    [onSelect]
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
      } else if (e.key === "ArrowDown" && !open && query.length >= 3) {
        setOpen(true);
      }
    },
    [open, query]
  );

  return (
    <div className={cn("flex gap-2", className)}>
      {/* Canton Selector */}
      {showCantonSelector && (
        <Select
          value={canton}
          onValueChange={handleCantonChange}
          disabled={disabled}
        >
          <SelectTrigger className="w-[100px]">
            <SelectValue placeholder="Canton" />
          </SelectTrigger>
          <SelectContent>
            {SWISS_CANTONS.map(c => (
              <SelectItem key={c.code} value={c.code}>
                {c.code}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Search Input with Popover */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={inputRef}
              value={query}
              onChange={e => handleInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => query.length >= 3 && setOpen(true)}
              placeholder={placeholder}
              disabled={disabled}
              className="pl-9 pr-20"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
              {isLoading && (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              )}
              {inputType === "uid" && (
                <Badge variant="secondary" className="text-xs">
                  UID
                </Badge>
              )}
            </div>
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 w-[var(--radix-popover-trigger-width)]"
          align="start"
          onOpenAutoFocus={e => e.preventDefault()}
        >
          <Command shouldFilter={false}>
            <CommandList>
              {results.length === 0 && !isLoading && query.length >= 3 && (
                <CommandEmpty>
                  {inputType === "uid"
                    ? "No company found with this UID"
                    : "No companies found matching your search"}
                </CommandEmpty>
              )}
              {results.length === 0 && !isLoading && query.length < 3 && (
                <CommandEmpty>
                  Type at least 3 characters to search
                </CommandEmpty>
              )}
              {results.length > 0 && (
                <CommandGroup
                  heading={
                    inputType === "uid" ? "Registry Result" : "Search Results"
                  }
                >
                  {results.map(company => (
                    <CommandItem
                      key={company.uid}
                      value={company.uid}
                      onSelect={() => handleSelect(company)}
                      className="cursor-pointer"
                    >
                      <div className="flex flex-col gap-1 flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="font-medium truncate">
                            {company.name}
                          </span>
                          {company.legalForm && (
                            <Badge
                              variant="outline"
                              className="text-xs shrink-0"
                            >
                              {company.legalForm}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="font-mono">{company.uid}</span>
                          {company.address?.city && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {company.address.city}
                              {company.address.canton &&
                                `, ${company.address.canton}`}
                            </span>
                          )}
                          {"mwstRegistered" in company &&
                            (company as { mwstRegistered?: boolean })
                              .mwstRegistered && (
                              <span className="flex items-center gap-1 text-success">
                                <CheckCircle2 className="h-3 w-3" />
                                MWST
                              </span>
                            )}
                        </div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export { SWISS_CANTONS };
